import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import {
  Button,
  Combobox,
  DateTimePicker,
  Input,
  Modal,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import {
  LEADERBOARD_SCOPES,
  LeaderboardDto,
  LeaderboardScope,
  LeaderboardTranslationDto,
  LeaderboardUpsertDto,
  PartnerDto,
} from '@/common/models/gamification'

import { TranslationTabs } from './shared/TranslationTabs'

interface CreateLeaderboardModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Existing CUSTOM board to edit, or null/undefined to create a new one. */
  board?: LeaderboardDto | null
  partners: PartnerDto[]
  onSubmit: (values: LeaderboardUpsertDto, id?: string) => void
}

interface ILeaderboardFormValues {
  scope: LeaderboardScope
  partnerId?: string
  startsAt: Date | null
  endsAt: Date | null
  ordinal: number
  translations: LeaderboardTranslationDto[]
}

const SCOPE_LABELS: Record<LeaderboardScope, string> = {
  FESTIVAL: 'Festival',
  PARTNER: 'Partner',
}

export const CreateLeaderboardModal = ({
  open,
  onOpenChange,
  board,
  partners,
  onSubmit,
}: CreateLeaderboardModalProps) => {
  const isEditMode = !!board

  const schema = yup.object().shape({
    scope: yup
      .string()
      .oneOf(LEADERBOARD_SCOPES, 'Scope is required')
      .required('Scope is required'),
    partnerId: yup.string().when('scope', {
      is: 'PARTNER',
      then: (s) => s.required('Partner is required'),
      otherwise: (s) => s.notRequired(),
    }),
    startsAt: yup.date().nullable().required('Start date is required'),
    endsAt: yup
      .date()
      .nullable()
      .required('End date is required')
      .test(
        'after-start',
        'End must be after start',
        function isAfterStart(value) {
          const { startsAt } = this.parent
          if (!value || !startsAt) return true
          return new Date(value).getTime() > new Date(startsAt).getTime()
        },
      ),
    ordinal: yup
      .number()
      .typeError('Order must be a number')
      .integer('Order must be a whole number')
      .min(0, 'Order cannot be negative')
      .required('Order is required'),
    translations: yup.array().default([]),
  })

  const defaultValues: ILeaderboardFormValues = {
    scope: 'FESTIVAL',
    partnerId: undefined,
    startsAt: null,
    endsAt: null,
    ordinal: 0,
    translations: [],
  }

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<ILeaderboardFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues,
  })

  const scope = watch('scope')

  useEffect(() => {
    if (!open) return
    if (board) {
      setValue('scope', board.scope, { shouldValidate: true })
      setValue('partnerId', board.partnerId ?? undefined, {
        shouldValidate: true,
      })
      setValue('startsAt', board.startsAt ? new Date(board.startsAt) : null, {
        shouldValidate: true,
      })
      setValue('endsAt', board.endsAt ? new Date(board.endsAt) : null, {
        shouldValidate: true,
      })
      setValue('ordinal', board.ordinal ?? 0, { shouldValidate: true })
      setValue('translations', board.translations ?? [], {
        shouldValidate: true,
      })
    }
    return () => {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, board])

  const handleClose = () => onOpenChange(false)

  const submit = (values: ILeaderboardFormValues) => {
    onSubmit(
      {
        scope: values.scope,
        partnerId: values.scope === 'PARTNER' ? values.partnerId : undefined,
        startsAt: (values.startsAt as Date).toISOString(),
        endsAt: (values.endsAt as Date).toISOString(),
        ordinal: values.ordinal,
        translations: values.translations,
      },
      board?.id,
    )
    handleClose()
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="lg"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="events-star"
            title={
              <span className="flex items-center gap-2">
                {isEditMode ? 'Edit' : 'Add New'}{' '}
                <span className="text-accent-1">Leaderboard</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button
            disabled={isEditMode && Object.keys(dirtyFields).length === 0}
            form="create-leaderboard-form"
            type="submit"
            variant="primary"
          >
            Save
          </Button>
        </div>
      }
    >
      <form
        id="create-leaderboard-form"
        onSubmit={handleSubmit(submit)}
        className="space-y-6"
      >
        <Controller
          name="scope"
          control={control}
          render={({ field }) => (
            <FormGroup label="Scope" error={errors?.scope?.message}>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex gap-6"
              >
                {LEADERBOARD_SCOPES.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 text-sm text-monochrome-2 cursor-pointer"
                  >
                    <RadioGroupItem value={s} />
                    {SCOPE_LABELS[s]}
                  </label>
                ))}
              </RadioGroup>
            </FormGroup>
          )}
        />

        {scope === 'PARTNER' && (
          <Controller
            name="partnerId"
            control={control}
            render={({ field }) => (
              <FormGroup label="Partner" error={errors?.partnerId?.message}>
                <Combobox
                  options={partners.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Select partner"
                  searchPlaceholder="Search partners..."
                  isInvalid={!!errors?.partnerId?.message}
                />
              </FormGroup>
            )}
          />
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Controller
              name="startsAt"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Starts at"
                  mode="datetime"
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!errors?.startsAt?.message}
                  maxDate={watch('endsAt')}
                />
              )}
            />
            {errors?.startsAt?.message && (
              <span className="text-xs text-accent-3">
                {errors.startsAt.message}
              </span>
            )}
          </div>
          <div>
            <Controller
              name="endsAt"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Ends at"
                  mode="datetime"
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!errors?.endsAt?.message}
                  minDate={watch('startsAt')}
                />
              )}
            />
            {errors?.endsAt?.message && (
              <span className="text-xs text-accent-3">
                {errors.endsAt.message}
              </span>
            )}
          </div>
        </div>

        <Controller
          name="ordinal"
          control={control}
          render={({ field }) => (
            <FormGroup label="Order" error={errors?.ordinal?.message}>
              <Input
                type="number"
                min={0}
                value={field.value ?? 0}
                onChange={(e) => field.onChange(e.target.value)}
                isInvalid={!!errors?.ordinal?.message}
                placeholder="Display order in the leaderboard list"
              />
            </FormGroup>
          )}
        />

        <Controller
          name="translations"
          control={control}
          render={({ field }) => (
            <TranslationTabs<LeaderboardTranslationDto>
              value={field.value}
              onChange={field.onChange}
              makeEmpty={(locale) => ({ locale, name: '' })}
              renderFields={({ value, onFieldChange }) => (
                <FormGroup label="Name">
                  <Input
                    value={value.name ?? ''}
                    onChange={(e) => onFieldChange({ name: e.target.value })}
                    type="text"
                    placeholder="Enter leaderboard name"
                  />
                </FormGroup>
              )}
            />
          )}
        />

        {isEditMode && (
          <div className="text-xs text-monochrome-2">
            Active custom leaderboards update live as points are awarded.
            Editing the window takes effect within about a minute — or click
            Regenerate on the list page to apply it instantly.
          </div>
        )}
      </form>
    </Modal>
  )
}
